/**
 * ModelListPage — layout aligned with Product list (search + status toolbar, dual pagination bars).
 */
import { useCallback, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronsUpDown, Loader2, Plus, Trash2, X } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { FilterToolbarSearch } from '@/components/ui/FilterToolbarSearch'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { EditIconButton } from '@/components/ui/EditIconButton'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { LIBERTY_FOCUS_INPUT } from '@/features/user-management/utils/libertyAdminUi'
import styles from '@/features/invitee/styles/invitesParity.module.css'
import configSortStyles from '@/components/listing/propertySortHeaderButton.module.css'
import { cn } from '@/lib/utils/helpers'
import { useUserRole } from '@/hooks/useUserRole'
import {
  getModelAddReactPath,
  getModelEditReactPath,
} from '@/lib/utils/modelNavigation'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import { MODEL_LIST_PER_PAGE_OPTIONS } from '../constants'
import { useModelList } from '../hooks/useModelList'
import { deleteModel, fetchCanDeleteModel } from '../services/modelService'
import type { ModelRecord, ModelSortKey } from '../types'
import {
  getModelDescriptionExcerpt,
  isBaseModel,
  isModelActive,
} from '../utils/modelListFormatters'

function ModelDescriptionCell({
  text,
  isLastRow,
}: {
  text: string
  isLastRow: boolean
}) {
  const full = text ?? ''
  const preview = getModelDescriptionExcerpt(full)
  const previewDisplay =
    preview && preview !== '—' && !preview.endsWith('...') ? `${preview}...` : preview === '—' ? '' : preview

  if (!previewDisplay) {
    return <td className="overflow-hidden text-slate-700">—</td>
  }

  return (
    <td className="overflow-hidden text-slate-700">
      <InfoPopover
        inline
        content={full}
        align="start"
        maxWidth={448}
        isLastRow={isLastRow}
        testId="model-description-popover"
        contentClassName="whitespace-pre-wrap text-sm leading-relaxed"
      >
        <button
          type="button"
          className="block w-full max-w-full cursor-default border-0 bg-transparent p-0 text-left font-inherit text-inherit text-slate-700"
          aria-label="Show full description"
        >
          <span className="block truncate">{previewDisplay}</span>
        </button>
      </InfoPopover>
    </td>
  )
}

function PaginationControls({
  currentPage,
  totalPages,
  onChangePage,
  testIdPrefix,
}: {
  currentPage: number
  totalPages: number
  onChangePage: (p: number) => void
  testIdPrefix: string
}) {
  const btn =
    'min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 enabled:hover:bg-slate-50 disabled:opacity-50 text-sm'
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end"
      data-testid={`${testIdPrefix}-pagination`}
    >
      <button type="button" className={btn} disabled={currentPage === 1} onClick={() => onChangePage(1)}>
        &laquo;
      </button>
      <button
        type="button"
        className={btn}
        disabled={currentPage === 1}
        onClick={() => onChangePage(currentPage - 1)}
      >
        &lsaquo;
      </button>
      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
        let p: number
        if (totalPages <= 5) p = i + 1
        else if (currentPage <= 3) p = i + 1
        else if (currentPage >= totalPages - 2) p = totalPages - 4 + i
        else p = currentPage - 2 + i
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChangePage(p)}
            className={cn(
              'min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium border',
              p === currentPage
                ? 'bg-[rgb(21_151_255)] text-white border-[rgb(21_151_255)]'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
            )}
          >
            {p}
          </button>
        )
      })}
      <button
        type="button"
        className={btn}
        disabled={currentPage === totalPages}
        onClick={() => onChangePage(currentPage + 1)}
      >
        &rsaquo;
      </button>
      <button
        type="button"
        className={btn}
        disabled={currentPage === totalPages}
        onClick={() => onChangePage(totalPages)}
      >
        &raquo;
      </button>
    </div>
  )
}

function ModelGotoPageInput({
  current,
  last,
  onChangePage,
  testId,
}: {
  current: number
  last: number
  onChangePage: (p: number) => void
  testId: string
}) {
  return (
    <input
      type="number"
      min={1}
      max={last}
      aria-label="Go to page"
      data-testid={testId}
      key={`${testId}-${current}`}
      defaultValue={current}
      className="w-14 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-center text-xs font-bold text-slate-900 tabular-nums focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20 sm:text-sm"
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      onBlur={(e) => {
        const parsed = parseInt(e.target.value, 10)
        if (Number.isNaN(parsed)) {
          e.target.value = String(current)
          return
        }
        const next = Math.min(last, Math.max(1, parsed))
        if (next !== current) onChangePage(next)
        e.target.value = String(next)
      }}
    />
  )
}

function ModelListPaginationBar({
  pageMeta,
  totalPages,
  queryPerPage,
  onChangePage,
  onChangePerPage,
  testIdPrefix,
}: {
  pageMeta: { current: number; total: number; last: number }
  totalPages: number
  queryPerPage: number
  onChangePage: (p: number) => void
  onChangePerPage: (n: number) => void
  testIdPrefix: string
}) {
  const currentPage = pageMeta.current || 1
  return (
    <div className="flex min-w-0 flex-col gap-2 border-slate-100 bg-slate-50/50 px-3 py-2.5 sm:gap-3 sm:px-6 sm:py-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-center gap-1.5 text-center text-xs font-medium text-slate-500 sm:justify-start sm:text-left sm:text-sm lg:max-w-md">
        <span>Page</span>
        {(pageMeta.last || 1) > 1 ? (
          <ModelGotoPageInput
            current={currentPage}
            last={pageMeta.last || 1}
            onChangePage={onChangePage}
            testId={`${testIdPrefix}-page-jump`}
          />
        ) : (
          <span className="font-bold text-slate-900">{currentPage}</span>
        )}
        <span>
          of <span className="font-bold text-slate-900">{pageMeta.last || 1}</span>
        </span>
        <span className="mx-2 text-slate-300">|</span>
        <span>
          Total <span className="font-bold text-slate-900">{pageMeta.total}</span> Records
        </span>
      </div>
      <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-2 lg:ml-auto lg:flex-1">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onChangePage={onChangePage}
          testIdPrefix={testIdPrefix}
        />
        <div className="flex shrink-0 items-center justify-center gap-1.5 sm:justify-end sm:gap-2 sm:pl-0 sm:ml-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Rows</span>
          <select
            className="min-w-0 rounded-lg border border-slate-200 bg-white p-1.5 text-xs text-slate-700 sm:p-2 sm:text-sm focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/20"
            value={queryPerPage}
            onChange={(e) => onChangePerPage(parseInt(e.target.value, 10))}
            data-testid={`${testIdPrefix}-per-page-select`}
          >
            {MODEL_LIST_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

function columnAriaSort(
  column: string,
  orderBy: string,
  sortBy: 'asc' | 'desc',
): 'ascending' | 'descending' | 'none' {
  if (orderBy !== column) return 'none'
  return sortBy === 'asc' ? 'ascending' : 'descending'
}

export function ModelListPage() {
  const navigate = useNavigate()
  const { hasPolicy } = useUserRole()
  const canViewListing = hasPolicy(['model_listing'])
  const canAddModel = hasPolicy(['model_add'])
  const canEditModel = hasPolicy(['model_edit'])
  const canDeleteModel = hasPolicy(['model_delete'])

  const {
    models,
    params,
    pagination,
    loading,
    error,
    searchInput,
    setSearchInput,
    statusFilter,
    handleStatusChange,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
  } = useModelList()

  const [deleteTarget, setDeleteTarget] = useState<ModelRecord | null>(null)
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('')
  const [deleteCheckingId, setDeleteCheckingId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)

  const totalPages = Math.max(1, pagination.last || 1)
  const statusFilterLabel = statusFilter === '1' ? 'Active' : statusFilter === '0' ? 'Inactive' : ''
  const hasActiveFilters = Boolean(statusFilter) || Boolean(params.search)

  const handleFilterSubmit = () => {
    applyFilters()
  }

  const handleResetAllFilters = () => {
    resetFilters()
  }

  const openEdit = (id: number) => {
    navigate(getModelEditReactPath(id))
  }

  const requestDelete = useCallback(
    async (model: ModelRecord) => {
      if (deleting || deleteCheckingId != null) return
      setDeleteCheckingId(model.model_id)
      try {
        const { allowed, message } = await fetchCanDeleteModel(model.model_id)
        if (!allowed) {
          showError(message)
          return
        }
        setDeleteConfirmMessage(message)
        setDeleteTarget(model)
      } catch (err) {
        showApiError(err)
      } finally {
        setDeleteCheckingId(null)
      }
    },
    [deleteCheckingId, deleting],
  )

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteModel(deleteTarget.model_id)
      showSuccess('Model deleted successfully')
      setDeleteTarget(null)
      refetch()
    } catch (err) {
      showApiError(err)
    } finally {
      setDeleting(false)
    }
  }

  const SortHeader = ({
    column,
    label,
    className,
    testId,
  }: {
    column: ModelSortKey
    label: string
    className?: string
    testId?: string
  }) => {
    const isActive = params.orderBy === column
    return (
      <th
        scope="col"
        className={cn('select-none !text-left !align-middle', !loading && 'cursor-pointer', className)}
        aria-sort={columnAriaSort(column, params.orderBy, params.sortBy)}
        data-testid={testId}
      >
        <button
          type="button"
          disabled={loading}
          className={cn(
            styles.sortHeaderButton,
            configSortStyles.propertySortHeaderButton,
            'justify-start',
          )}
          onClick={() => sort(column)}
        >
          <span className={styles.sortHeaderLabel}>{label}</span>
          <span
            aria-hidden="true"
            className={cn(styles.sortIcon, isActive && styles.sortIconActive)}
          >
            {isActive ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
          </span>
        </button>
      </th>
    )
  }

  if (!canViewListing) {
    return (
      <div className="min-h-screen bg-[#f8fafc] pb-12 font-sans text-slate-900">
        <PageHeader title="MODELS" testIdRoot="model-list" />
        <div className="w-full px-3 py-6 sm:px-6">
          <p className="text-slate-600" data-testid="model-list-no-listing-access">
            You do not have permission to view the model listing.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen min-w-0 bg-[#f8fafc] pb-12 font-sans text-slate-900"
      data-testid="model-list-page"
    >
      <PageHeader title="MODELS" testIdRoot="model-list" />

      <div className="mx-auto flex w-full min-w-0 flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-6 sm:py-8">
        <form
          data-testid="model-list-toolbar"
          className="flex w-full min-w-0 flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-nowrap sm:gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            handleFilterSubmit()
          }}
        >
          <FilterToolbarSearch
            data-testid="model-list-search-wrap"
            inputDataTestId="model-list-search-input"
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search models"
            label="Search"
            wrapClassName="min-w-0 max-w-sm flex-1"
            inputClassName="focus:border-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]/15"
          />
          <div className="relative shrink-0">
            <select
              id="model-list-status"
              aria-label="Status"
              className={cn(
                'h-[42px] appearance-none rounded-xl border border-slate-200 bg-white pl-3 pr-8 text-sm leading-none shadow-sm',
                LIBERTY_FOCUS_INPUT,
              )}
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              data-testid="model-list-status-select"
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
              aria-hidden
            />
          </div>
          {statusFilterLabel && (
            <span
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[rgb(21,151,255)]/30 bg-[rgb(21,151,255)]/10 px-3 py-2 text-sm font-semibold text-[rgb(21_151_255)]"
              data-testid="model-list-status-chip"
            >
              {statusFilterLabel}
              <button
                type="button"
                onClick={() => handleStatusChange('')}
                aria-label={`Remove ${statusFilterLabel} filter`}
                className="rounded-full p-0.5 hover:bg-[rgb(21,151,255)]/20"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </span>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleResetAllFilters}
              className="shrink-0 text-sm font-semibold text-[rgb(21_151_255)] hover:underline"
              data-testid="model-list-reset-button"
            >
              Clear filters
            </button>
          )}
          <button type="submit" disabled={loading} className="sr-only" data-testid="model-list-filter-submit">
            Apply
          </button>
          <span
            className="ml-auto shrink-0 whitespace-nowrap text-sm text-slate-500"
            data-testid="model-list-count"
          >
            <span className="font-semibold text-slate-900">{pagination.total}</span> models
          </span>
          {canAddModel && (
            <Link
              to={getModelAddReactPath()}
              className={cn(primaryEntityActionButtonClass, 'shrink-0')}
              data-testid="model-list-add-button"
            >
              <Plus className="h-5 w-5 shrink-0" aria-hidden />
              Add New Model
            </Link>
          )}
        </form>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
          </div>
        )}

        {!loading && !error && (
          <div className={styles.tableCard} data-testid="model-list-table-card">
            <ModelListPaginationBar
              pageMeta={pagination}
              totalPages={totalPages}
              queryPerPage={params.perPage}
              onChangePage={changePage}
              onChangePerPage={changePerPage}
              testIdPrefix="model-list-top"
            />

            <div
              className={cn(styles.tableResponsiveScroll, 'touch-scroll')}
              data-testid="model-list-table-container"
            >
              <table
                data-testid="model-list-table"
                className={cn(
                  styles.listingTable,
                  styles.propertyListingTable,
                  'table-fixed w-full [&_thead_th]:!whitespace-nowrap [&_tbody_td:not([colspan])]:!whitespace-normal',
                  '[&_tbody_td:not([colspan])]:break-words',
                  '[&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle',
                  '[&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug',
                )}
              >
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '40%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <SortHeader
                      column="model_id"
                      label="Model ID"
                      testId="model-list-sort-model-id"
                    />
                    <SortHeader
                      column="model_name"
                      label="Model Name"
                      testId="model-list-sort-name"
                    />
                    <th
                      scope="col"
                      className="!text-left !align-middle"
                      data-testid="model-list-column-description-header"
                    >
                      Description
                    </th>
                    <SortHeader
                      column="is_active"
                      label="Status"
                      testId="model-list-sort-status"
                    />
                    <th
                      scope="col"
                      className="!text-center !align-middle"
                      data-testid="model-list-column-action-header"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {models.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="!py-16 text-center text-slate-500">
                        No Models Found
                      </td>
                    </tr>
                  ) : (
                    models.map((model, rowIndex) => {
                      const showDelete = canDeleteModel && !isBaseModel(model.model_id)
                      const isCheckingDelete = deleteCheckingId === model.model_id

                      return (
                        <tr
                          key={model.model_id}
                          data-testid={`model-list-row-${model.model_id}`}
                          className="transition-colors"
                        >
                          <td
                            className="whitespace-nowrap tabular-nums text-slate-700"
                            data-testid={`model-list-row-${model.model_id}-id`}
                          >
                            {model.model_id}
                          </td>
                          <td>
                            {canEditModel ? (
                              <Link
                                to={getModelEditReactPath(model.model_id)}
                                className="text-[13px] font-medium text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline"
                                data-testid={`model-list-row-${model.model_id}-name-link`}
                              >
                                {model.model_name}
                              </Link>
                            ) : (
                              <span className="text-[13px] font-medium text-slate-900">
                                {model.model_name}
                              </span>
                            )}
                            {isBaseModel(model.model_id) && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-300">
                                Default
                              </span>
                            )}
                          </td>
                          <ModelDescriptionCell
                            text={String(model.description ?? '')}
                            isLastRow={rowIndex === models.length - 1}
                          />
                          <td>
                            <span
                              className={cn(
                                'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold',
                                isModelActive(model.is_active)
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-red-100 text-red-700',
                              )}
                            >
                              {isModelActive(model.is_active) ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="!text-center">
                            <div className="mx-auto flex w-[4.25rem] items-center justify-start gap-1">
                              {canEditModel && (
                                <EditIconButton
                                  onClick={() => openEdit(model.model_id)}
                                  data-testid={`model-list-row-${model.model_id}-edit-link`}
                                />
                              )}
                              {showDelete && (
                                <button
                                  type="button"
                                  title="Delete"
                                  aria-label="Delete"
                                  disabled={isCheckingDelete || deleting}
                                  onClick={() => void requestDelete(model)}
                                  className="inline-flex items-center justify-center rounded-full bg-transparent p-1 text-rose-600 transition hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
                                  data-testid={`model-list-row-${model.model_id}-delete-button`}
                                >
                                  {isCheckingDelete ? (
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                  ) : (
                                    <Trash2 className="h-4 w-4" aria-hidden />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            <ModelListPaginationBar
              pageMeta={pagination}
              totalPages={totalPages}
              queryPerPage={params.perPage}
              onChangePage={changePage}
              onChangePerPage={changePerPage}
              testIdPrefix="model-list-bottom"
            />
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteTarget != null}
        title="Delete Model"
        message={deleteConfirmMessage || 'Are you sure you want to delete this model?'}
        primaryLabel="Delete"
        onPrimary={() => void confirmDelete()}
        secondaryLabel="Cancel"
        onSecondary={() => {
          if (!deleting) setDeleteTarget(null)
        }}
        onDismiss={() => {
          if (!deleting) setDeleteTarget(null)
        }}
        variant="warning"
      />
    </div>
  )
}
